/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRequestContext } from '@msdyn365-commerce/core';

// import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import { VariantType } from '../isMobile/isMobile.props';
import { isMobile } from '../isMobile/isMobile';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, TelemetryConstant } from '../common/telemetryHelper';
import { IIncrementalQuantityProps } from './IncrementalQuantity.props';

declare global {
    interface Window {
        quantityDebouncer: NodeJS.Timeout;
    }
}

/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
    isMultipleOfQuantity: boolean;
    isRecalculating: boolean;
    isChanged: boolean;
    isObserverCallbackTriggered: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        min: 1,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private _minValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly payLoad: IPayLoad;

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityProps>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (
            (!props.disabled && state.isUpdating) ||
            ((props.applyDefaultOrderSettings ?? false) &&
                state.currentInput === 1 &&
                props.currentCount &&
                props.currentCount > IncrementalQuantity.defaultProps.min!)
        ) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }

        return nextState;
    }

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        const multipleOfQuantity = this._getMultipleOfQuantity();
        const { currentCount = 0 } = props;
        this.state = {
            isMultipleOfQuantity: true,
            currentInput: currentCount > 0 ? currentCount : multipleOfQuantity > 0 ? multipleOfQuantity : 1,
            isUpdating: false,
            isRecalculating: false,
            isChanged: false,
            isObserverCallbackTriggered: false
        };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        // this._handleChange = this._handleChange.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = this.props.min ?? IncrementalQuantity.defaultProps.min!;
    }

    public componentDidMount() {
        const itemCounter = document.querySelector('.msc-cart-lines-group-wrapper__bopis-heading-title-ct');
        if (itemCounter) {
            const observer = new MutationObserver(async () => {
                if (this.state.isObserverCallbackTriggered) {
                    this.setState({ isRecalculating: false });
                    const cart = this.props.props.data.cartState;
                    if (cart) {
                        cart.updateExtensionProperties({
                            newExtensionProperties: [{ Key: 'DidRecalculate', Value: { BooleanValue: undefined } }]
                        });
                    }
                }
                this.setState({ isObserverCallbackTriggered: false, isMultipleOfQuantity: true });
                this.props.quantityChecks.setIsValidQuantities(state => state.map(() => true));
            });
            observer.observe(itemCounter, { subtree: true, characterData: true });
        }
    }

    public render(): JSX.Element {
        const { min, max } = this.props;

        this._minValue = min ?? IncrementalQuantity.defaultProps.min!;
        const multipleOfQuantity = this._getMultipleOfQuantity();
        const value = isNaN(this.state.currentInput) ? multipleOfQuantity : this.state.currentInput;

        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const decrementDisabled = value <= multipleOfQuantity || this.props.isGiftCard;
        const incrementDisabled = value >= Math.floor(max / multipleOfQuantity) * multipleOfQuantity || this.props.isGiftCard;
        const currentValue = value;
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${value}`;

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity' id={this.props.id}>
                    <button
                        disabled={this.props.disabled || decrementDisabled || this.state.isRecalculating}
                        title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                        className={`decrement quantity__controls ${extraClassDecrement}${this.state.isRecalculating ? ' disabled' : ''}`}
                        onClick={this._onDecrement}
                        aria-hidden={!!decrementDisabled}
                        aria-label={`${this.props.decrementButtonAriaLabel}`}
                        color='secondary'
                        {...decrementAttributes}
                    >
                        <span className={glyphMinusClassName} />
                    </button>
                    <input
                        type='number'
                        className={`quantity-input${this.state.isRecalculating ? ' disabled' : ''}`}
                        pattern='[0-9]*'
                        value={value !== 0 ? value.toString().replace(/^0*/g, '') : 0}
                        onChange={event => {
                            const val = parseInt(event.currentTarget.value.replace(/^0*/g, ''));
                            const newInput = isNaN(val) ? 0 : val > this.props.max ? this.props.max : val;
                            const isMultipleOfQuantity =
                                this._getMultipleOfQuantity() > 0 && newInput % this._getMultipleOfQuantity() === 0;
                            this.setState({
                                isMultipleOfQuantity: isMultipleOfQuantity,
                                isChanged: true,
                                currentInput: newInput
                            });
                            this.props.quantityChecks.setIsValidQuantities(state => {
                                const clone = [...state];
                                clone[this.props.quantityChecks.index] = val > 0 && isMultipleOfQuantity;
                                return clone;
                            });
                        }}
                        step={this._getMultipleOfQuantity()}
                        onBlur={async () => {
                            if (
                                this.props.onChange &&
                                this.state.currentInput > 0 &&
                                this.state.isChanged &&
                                this.state.isMultipleOfQuantity
                            ) {
                                this.props.onChange(this.state.currentInput);
                                this.setState({
                                    isRecalculating: this.props.currentCount !== this.state.currentInput,
                                    isObserverCallbackTriggered: true
                                });
                            }
                            this.setState({ isChanged: false });
                        }}
                        aria-live='polite'
                        aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                        role='spinbutton'
                        aria-valuemin={this._minValue}
                        aria-valuemax={max}
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled || this.state.isRecalculating}
                        min={this._minValue}
                        max={max}
                    />
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={`increment quantity__controls ${extraClassIncrement}${this.state.isRecalculating ? ' disabled' : ''}`}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${this.props.incrementButtonAriaLabel}`}
                        color='secondary'
                        {...incrementAttributes}
                    >
                        <span className={glyphPlusClassName} />
                    </button>
                </div>
                {this.state.currentInput === 0 ? (
                    <div className='quantity__error'>Quantity must be positive or remove the item from cart</div>
                ) : this.state.isMultipleOfQuantity ? (
                    this.state.isRecalculating ? (
                        <div className='quantity__recalculating'>Recalculating...</div>
                    ) : null
                ) : (
                    <div className='quantity__error'>Please enter quantity in the increment of {multipleOfQuantity}</div>
                )}
            </>
        );
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        const multipleOfQuantity = this._getMultipleOfQuantity();
        let updatedQuantity: number;

        if (currQuantity < this.props.max) {
            invokeCallback = true;
            const nextValue =
                multipleOfQuantity > 0 ? (Math.floor(currQuantity / multipleOfQuantity) + 1) * multipleOfQuantity : currQuantity + 1;
            updatedQuantity = nextValue < this.props.max ? nextValue : this.props.max;
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        this.setState({ currentInput: updatedQuantity });

        clearTimeout(window.quantityDebouncer);
        window.quantityDebouncer = setTimeout(async () => {
            if (invokeCallback && this.props.onChange) {
                this.props.onChange(updatedQuantity);
                this.setState({ isRecalculating: this.props.currentCount !== this.state.currentInput, isObserverCallbackTriggered: true });
                this.props.quantityChecks.setIsValidQuantities(state => {
                    const clone = [...state];
                    clone[this.props.quantityChecks.index] = updatedQuantity > 0 && this.state.isMultipleOfQuantity;
                    return clone;
                });
            }
        }, 400);

        this.setState({ isMultipleOfQuantity: true });
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        const multipleOfQuantity = this._getMultipleOfQuantity();
        const min =
            this._minValue >= multipleOfQuantity
                ? multipleOfQuantity > 1 // check for >1 because MOQ expression will not allow quantity of less than 2 for items with multiple of 1
                    ? (Math.floor(this._minValue / multipleOfQuantity) + 1) * multipleOfQuantity
                    : this._minValue - 1
                : multipleOfQuantity;
        let updatedQuantity: number;

        if (currQuantity >= min) {
            invokeCallback = true;
            const nextValue =
                multipleOfQuantity > 0 ? (Math.floor(currQuantity / multipleOfQuantity) - 1) * multipleOfQuantity : currQuantity - 1;
            updatedQuantity = nextValue >= min ? nextValue : min;
        } else {
            invokeCallback = false;
            updatedQuantity = min;
        }

        this.setState({ currentInput: updatedQuantity });

        clearTimeout(window.quantityDebouncer);
        window.quantityDebouncer = setTimeout(async () => {
            if (invokeCallback && this.props.onChange) {
                this.props.onChange(updatedQuantity);
                this.setState({ isRecalculating: this.props.currentCount !== this.state.currentInput, isObserverCallbackTriggered: true });
                this.props.quantityChecks.setIsValidQuantities(state => {
                    const clone = [...state];
                    clone[this.props.quantityChecks.index] = updatedQuantity > 0 && this.state.isMultipleOfQuantity;
                    return clone;
                });
            }
        }, 400);

        this.setState({ isMultipleOfQuantity: true });
    }

    // private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    //     const multipleOfQuantity = this._getMultipleOfQuantity();
    //     let currentValue = Number.parseInt(e.target.value, 10);
    //     if (Number.isNaN(currentValue)) {
    //         this.setState({ currentInput: currentValue });
    //         return;
    //     }
    //     currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
    //     this.setState({
    //         currentInput: currentValue,
    //         isMultipleOfQuantity: multipleOfQuantity > 0 ? currentValue % multipleOfQuantity === 0 : true
    //     });
    // if (currentValue % multipleOfQuantity === 0)
    //     debounce(() => {
    //         // Due of usage debouncer, we may ignore onChange return value
    //         this.props.onChange && this.props.onChange(this.state.currentInput);

    //         const inputElement =
    //             this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
    //         if (inputElement) {
    //             inputElement.setAttribute('aria-valuenow', this.state.currentInput.toString());
    //             inputElement.setAttribute('value', this.state.currentInput.toString());
    //         }
    //     }, 1000)();
    // }

    /**
     * Validates min and updates.
     */
    // private readonly _validateMin = (): void => {
    //     if (Number.isNaN(this.state.currentInput)) {
    //         this.setState({ currentInput: this._minValue });
    //     } else {
    //         const currentValue = Math.min(this.props.max, Math.max(this._minValue, this.state.currentInput));
    //         if (currentValue !== this.state.currentInput) {
    //             this.setState({ currentInput: currentValue });
    //         }
    //     }
    // };

    private _getMultipleOfQuantity() {
        return this.props.product?.Behavior?.MultipleOfQuantity ?? 0;
    }
}
